import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {  } from "@thkStore/Components/ConfirmAddressModal"
import { useMergeStyles } from "@thkStore/Overrides/additionalStyles";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {    
    image = "image",
    focalPoint = "focalPoint",    
    heading = "heading",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",    
    disableButton = "disableButton",
    textAlignment = "textAlignment",
    textColor = "textColor",
    textBackgroundColor = "textBackgroundColor",
    contentPadding = "contentPadding",
    centerTextVertically = "centerTextVertically",
    imageSide = "imageSide",
    textSide = "textSide",
    minimumHeight = "minimumHeight",    
}

interface OwnProps extends WidgetProps {
    fields: {        
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";        
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;        
        [fields.disableButton]: boolean;
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.textColor]: string;
        [fields.textBackgroundColor]: string;
        [fields.contentPadding]: number;
        [fields.centerTextVertically]: boolean;
        [fields.imageSide]: "left" | "right";
        [fields.textSide]: "left" | "right";
        [fields.minimumHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport"; 
    };
    extendedStyles?: SplitBannerStyles;
}

export interface SplitBannerStyles {
    wrapper?: InjectableCss;
    leftPanel?: InjectableCss;
    rightPanel?: InjectableCss;
    imagePanel?: InjectableCss;
    textPanel?: InjectableCss;
    textContentWrapper?: InjectableCss;
    bannerButton?: ButtonPresentationProps;
}

export const splitBannerStyles: SplitBannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;

            @media (max-width: 768px) {
                flex-direction: column;
            }
        `,
    },
    leftPanel: {
        css: css`
            flex: 1;
            display: flex;

            @media (max-width: 768px) {
                width: 100%;
                min-height: 200px;
            }
        `,
    },
    rightPanel: {
        css: css`
            flex: 1;
            display: flex;

            @media (max-width: 768px) {
                width: 100%;
            }
        `,
    },
    imagePanel: {
        css: css`
            width: 100%;
            height: 100%;
            background-repeat: no-repeat;
            background-size: cover;
            position: relative;
        `,
    },
    textPanel: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
        `,
    },
    textContentWrapper: {
        css: css`
            width: 100%;

            .split-banner-heading{
                font-size: 36px;
                line-height: 1;
                margin-bottom: 30px;
                position: relative;
                padding-left: 16px;
                margin-left: -4px;

                @media (max-width:768px){
                    margin-bottom: 24px;
                }

                &:before{
                    content: "|";
                    position: absolute;
                    left: 0;
                    top: -8px;
                    font-size: 46px;

                    @media (max-width:768px){
                        top: -12px;
                    }
                }

                p{
                    font-size: 36px;
                    font-weight: 600;
                    line-height: 1;

                    @media (max-width:768px){
                        font-size: 24px;
                    }
                }
            }

            .split-banner-subheading{
                font-size: 16px;

                @media (max-width:768px){
                    font-size: 12px;
                }
            
                p{
                    font-size: 16px;

                    @media (max-width:768px){
                        font-size: 12px;
                    }
                }
            }
        `,
    },
};

export const SplitBanner: FC<OwnProps> = ({ fields: widgetFields, extendedStyles }) => {
    const { url, title } = useGetLink(widgetFields.buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();
    const styles = useMergeStyles("splitBanner", splitBannerStyles, extendedStyles);

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    

    const imgUrl = widgetFields.image ? new URL(widgetFields.image) : null;
    const backgroundStyles = imgUrl ? `background-image: url(${imgUrl.pathname}${imgUrl.search});` : "";        

    const focalPointStyles = getFocalPointStyles(widgetFields.focalPoint);

    let minimumHeightStyles;
    switch (widgetFields.minimumHeight) {
        case "1/4 viewport":
            minimumHeightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            minimumHeightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            minimumHeightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            minimumHeightStyles = "min-height: 100vh;";
            break;
    }

    let fontSizeStyles;

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${minimumHeightStyles}
            
            margin-bottom: 24px;

            @media (max-width:768px){
                margin-bottom: 0;
            }
        `,
    };

    const imagePanelStyles = {
        css: css`
            ${styles.imagePanel?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
        `,
    };    

    const textPanelStyles = {
        css: css`
            ${styles.textPanel?.css || ""}
            background-color: ${widgetFields.textBackgroundColor || "transparent"};
            color: ${widgetFields.textColor || "inherit"};
            text-align: ${widgetFields.textAlignment || "left"};
            ${
                widgetFields.centerTextVertically
                    ? `
                align-items: center;
                justify-content: center;
            `
                    : ""
            }
            ${fontSizeStyles}
        `,
    };

    const textContentWrapperStyles = {
        css: css`
            ${styles.textContentWrapper?.css || ""}
            padding: 93px 48px 93px 32px;
            @media (max-width:768px){
                padding: 36px 16px 24px;
            }
            padding: ${widgetFields.contentPadding}px;
            
            .split-banner-button{
                margin-top: 20px;

                &:hover{
                    background-color: ${widgetFields.textColor || "transparent"} !important;
                    color: ${widgetFields.textBackgroundColor || "inherit"};
                    opacity: 0.5;
                }
            }
        `,
    };

    const ImagePanel = (
        <StyledWrapper className="split-banner-image-panel" {...imagePanelStyles}>            
        </StyledWrapper>
    );

    const TextPanel = (
        <StyledWrapper className="split-banner-text-panel" {...textPanelStyles}>
            <StyledWrapper className="split-banner-text-content" {...textContentWrapperStyles}>
                <Typography as="h1" className="split-banner-heading">{parse(widgetFields.heading, parserOptions)}</Typography>
                <Typography className="split-banner-subheading">
                    {parse(widgetFields.subheading, parserOptions)}
                </Typography>
                {!widgetFields.disableButton && (
                    <Button className="split-banner-button" {...styles.bannerButton} onClick={onClick}>
                        {widgetFields.buttonLabel || title || url}
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );

    const renderPanels = () => {
        if (widgetFields.imageSide === "left" && widgetFields.textSide === "right") {
            return (
                <>
                    <StyledWrapper className="split-banner-left" {...styles.leftPanel}>
                        {ImagePanel}
                    </StyledWrapper>
                    <StyledWrapper className="split-banner-right" {...styles.rightPanel}>
                        {TextPanel}
                    </StyledWrapper>
                </>
            );
        }
        
        if (widgetFields.imageSide === "right" && widgetFields.textSide === "left") {
            return (
                <>
                    <StyledWrapper className="split-banner-left" {...styles.leftPanel}>
                        {TextPanel}
                    </StyledWrapper>
                    <StyledWrapper className="split-banner-right" {...styles.rightPanel}>
                        {ImagePanel}
                    </StyledWrapper>
                </>
            );
        }
        
        if (widgetFields.imageSide === "left" && widgetFields.textSide === "left") {
            return (
                <>
                    <StyledWrapper className="split-banner-left" {...styles.leftPanel}>
                        <StyledWrapper
                            {...{
                                css: css`
                                    position: relative;
                                    width: 100%;
                                `,
                            }}
                        >
                            {ImagePanel}
                            <StyledWrapper
                                {...{
                                    css: css`
                                        position: absolute;
                                        top: 0;
                                        left: 0;
                                        width: 100%;
                                        height: 100%;
                                    `,
                                }}
                            >
                                {TextPanel}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper className="split-banner-right" {...styles.rightPanel} />
                </>
            );
        }

        return (
            <>
                <StyledWrapper className="split-banner-left" {...styles.leftPanel} />
                <StyledWrapper className="split-banner-right" {...styles.rightPanel}>
                    <StyledWrapper
                        {...{
                            css: css`
                                position: relative;
                                width: 100%;
                            `,
                        }}
                    >
                        {ImagePanel}
                        <StyledWrapper
                            {...{
                                css: css`
                                    position: absolute;
                                    top: 0;
                                    left: 0;
                                    width: 100%;
                                    height: 100%;
                                `,
                            }}
                        >
                            {TextPanel}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </>
        );        
    };

    return (
        <StyledWrapper className="split-banner-wrapper" {...wrapperStyles}>
            {renderPanels()}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const layoutTab = {
    displayName: "Layout",
    sortOrder: 1,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const widgetModule: WidgetModule = {
    component: SplitBanner,
    definition: {
        group: "Basic",
        icon: "columns",
        fieldDefinitions: [            
            {
                fieldType: "Contextual",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",                
                tab: basicTab,
                isRequired: true,
            },
            {
                fieldType: "Contextual",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,                
                tab: basicTab,
            },           
            {
                fieldType: "Contextual",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },

            // === LAYOUT TAB ===
            {
                fieldType: "Contextual",
                name: fields.imageSide,
                displayName: "Image/Background Side",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.textSide,
                displayName: "Text Content Side",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "right",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.minimumHeight,
                displayName: "Minimum Banner Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.textAlignment,
                displayName: "Text Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.textBackgroundColor,
                displayName: "Text Panel Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.contentPadding,
                editorTemplate: "IntegerField",
                displayName: "Content Padding",
                defaultValue: 50,
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.centerTextVertically,
                editorTemplate: "CheckboxField",
                displayName: "Center Text Vertically",
                defaultValue: true,
                tab: layoutTab,
            },                       
        ],
    },
};

export default widgetModule;
